import { ReimbursementCategory } from "../types";

export type date = string; // ISO 8601 format (YYYY/MM/DD)

export interface ReimbursementItem {
  item: string;
  category: ReimbursementCategory;
  date: date;
  price: number;
  vendor: string;
}

export interface ReimbursementResponse {
  uuid: string;
}

export interface Reimbursement {
  dateSubmitted: string;
  attachments: string[];
  items: string[];
  status: string;
  errorMsg: string;
}

export interface User {
  id?: number;
  username: string;
  roles: string[];
  firstName?: string;
  lastName?: string;
  email?: string;
}

// Leave Management role checking utilities
export const hasLeaveRole = (user: User | null, role: string): boolean => {
  if (!user || !user.roles) return false;
  return user.roles.some((r) => r.includes(role));
};

export const canAccessLeaveManagement = (user: User | null): boolean => {
  return (
    hasLeaveRole(user, "EMPLOYEE") ||
    hasLeaveRole(user, "APPROVER") ||
    hasLeaveRole(user, "ADMIN")
  );
};

export const canApproveLeave = (user: User | null): boolean => {
  return hasLeaveRole(user, "APPROVER") || hasLeaveRole(user, "ADMIN");
};

export const canManageLeaveCredits = (user: User | null): boolean => {
  return hasLeaveRole(user, "ADMIN");
};

export const canViewAdminReports = (user: User | null): boolean => {
  return hasLeaveRole(user, "ADMIN");
};

export const canManageUsers = (user: User | null): boolean => {
  return hasLeaveRole(user, "ADMIN");
};

// Time Tracking role utilities
export const canAccessTimeTracking = (user: User | null): boolean => {
  // All authenticated users can access time tracking (per requirements)
  return user !== null;
};

export const canViewAllTimeStatuses = (user: User | null): boolean => {
  // All users can view colleague statuses (per requirements)
  return user !== null;
};

export const canManageTimeEntries = (user: User | null): boolean => {
  return hasLeaveRole(user, "ADMIN");
};

export const canForceTimeOut = (user: User | null): boolean => {
  return hasLeaveRole(user, "ADMIN");
};

export const canExportTimeData = (user: User | null): boolean => {
  return hasLeaveRole(user, "ADMIN");
};

export interface UserPreference {
  id?: number;
  employeeId?: number;
  themeMode: string; // "light" or "dark"
  language: string; // "en", "ja", "fil", "vi"
  settings?: string; // JSON string for extendable UI settings
  // Format: {"displayMode": "dock", "dockPosition": "right"}
}

export interface LoginInfo {
  user?: User;
  preferences?: UserPreference;
}

export interface Login {
  username: string;
  password: string;
}

export enum CurrencyCode {
  JPY = "JPY",
  VND = "VND",
  USD = "USD",
}

export interface ForEx {
  from: CurrencyCode;
  to: CurrencyCode;
  rate: string;
}

export interface Invoice {
  fileName: string;
  dateSubmitted: string;
  status: string;
}
